import React from 'react';
import {IWayPointCreationSingleVoyage} from "@containers/singleVoyage/types/point";
import {sx} from './sx'
import PrimaryButtonSingleVoyage
    from "@containers/singleVoyage/ui/buttonts/primaryButtonSingleVoyage/PrimaryButtonSingleVoyage";
import {useTranslation} from 'react-i18next';
import ActionUiHandler from "@ui/template/actionUiHandler/ActionUiHandler";
import InputSingleVoyage from "@containers/singleVoyage/ui/inputs/InputSingleVoyage";
import moment from 'moment';
import {DATE_FORMAT_POINT_SINGLE_VOYAGE} from "@containers/singleVoyage/containers/points/constants/dateFormat";
import MuiDatePicker from "@ui/datePicker/muiDatePicker/MuiDatePicker";
import DividerCustom from "@ui/dividerCustom/DividerCustom";
import AltRouteIcon from '@mui/icons-material/AltRoute';
import {useValidationValue} from "@hooks/validate/useValidationValue";
import {object} from 'yup';
import {numberMin0Schema, stringReqSchema} from "@helpers/validate/validateSchema";
import {getErrorProps} from "@helpers/error";
import {getRandomNumber} from "@helpers/number/getRandomNumber";
import {getPointGeomCoordinatesFromLatLng} from "@helpers/geoJson/getPointGeomCoordinatesFromLatLng";
import SwitchCustom from "@ui/switchCustom/SwitchCustom";
import {IStateMutatePointMutateSingleVoyage} from "@containers/singleVoyage/mutateVoyage/points/mutatePoint/types";

interface IProps {
    initial?: IWayPointCreationSingleVoyage,
    onSave?: (point: IWayPointCreationSingleVoyage) => void,
    closeModalFn?: () => void
    stateMutatePointMutateSingleVoyage: IStateMutatePointMutateSingleVoyage
    btnLabel?: string
}

const BaseMutatePointMutateSingleVoyage = ({
                                               initial,
                                               onSave,
                                               closeModalFn,
                                               stateMutatePointMutateSingleVoyage,
                                               btnLabel,
                                           }: IProps) => {

    const {t} = useTranslation()

    const [validateFn, error, errorMessage] = useValidationValue({
        value: {
            name: stateMutatePointMutateSingleVoyage.name,
            address: stateMutatePointMutateSingleVoyage.address,
            radius: stateMutatePointMutateSingleVoyage.radius,
        },
        validateSchema: object({
            name: stringReqSchema,
            address: stringReqSchema,
            radius: numberMin0Schema,
        }),
        dependency: [stateMutatePointMutateSingleVoyage.name, stateMutatePointMutateSingleVoyage.address, stateMutatePointMutateSingleVoyage.radius]
    })
    const onSaveFn = () => {
        const validate = validateFn()
        if (!validate) {
            return
        }
        onSave({
            ...initial,
            name: stateMutatePointMutateSingleVoyage.name,
            address: stateMutatePointMutateSingleVoyage.address,
            radius: Number(stateMutatePointMutateSingleVoyage.radius),
            type: initial?.type || 'POINT_RADIUS',
            plannedDeparture:stateMutatePointMutateSingleVoyage.hasPlannedDeparture ? moment(stateMutatePointMutateSingleVoyage.plannedDeparture, DATE_FORMAT_POINT_SINGLE_VOYAGE).toISOString() : null,
            plannedArrival: stateMutatePointMutateSingleVoyage.hasPlannedArrival ? moment(stateMutatePointMutateSingleVoyage.plannedArrival, DATE_FORMAT_POINT_SINGLE_VOYAGE).toISOString() : null,
            id: initial?.id || getRandomNumber(),
            plannedOrdinal: initial?.plannedOrdinal || 0,
            geom: initial?.geom ? initial.geom : getPointGeomCoordinatesFromLatLng({
                lat: 0,
                lng: 0,
            }),
            status: initial?.status || 'PLANNED',
        })
        closeModalFn && closeModalFn()
    }
    const isPlanned = !initial?.status ||  initial.status === 'PLANNED'
    return (
        <ActionUiHandler
            sx={sx.root}
        >
            {/* the name is the stop's own, so it stays editable even for a template stop -
                the waypointTemplateId is kept, only address and radius come from the template */}
            <InputSingleVoyage
                value={stateMutatePointMutateSingleVoyage.name || ''}
                onChange={stateMutatePointMutateSingleVoyage.onChangeName}
                label={t('base.name')}
                {...getErrorProps({errorMessage, error, key: 'name'})}
            />

            <InputSingleVoyage
                value={stateMutatePointMutateSingleVoyage.address || ''}
                onChange={stateMutatePointMutateSingleVoyage.onChangeAddress}
                label={t('base.address')}
                {...getErrorProps({errorMessage, error, key: 'address'})}
                disabled={!!initial?.waypointTemplateId}
            />

            <InputSingleVoyage
                value={stateMutatePointMutateSingleVoyage.radius || ''}
                onChange={stateMutatePointMutateSingleVoyage.onChangeRadius}
                label={t('base.entryRadius')}
                {...getErrorProps({errorMessage, error, key: 'radius'})}
                disabled={!!initial?.waypointTemplateId}
            />

            {isPlanned && <>
                <DividerCustom
                    label={t('singleVoyage.plannedStay')}
                    sx={sx.divider}
                    icon={<AltRouteIcon/>}
                    chipColor={'info'}
                />

                <SwitchCustom
                    checked={stateMutatePointMutateSingleVoyage.hasPlannedArrival}
                    onChange={stateMutatePointMutateSingleVoyage.onChangeHasPlannedArrival}
                    label={t('base.hasTimeStart')}
                />

                {stateMutatePointMutateSingleVoyage.hasPlannedArrival && <MuiDatePicker
                    value={stateMutatePointMutateSingleVoyage.plannedArrival}
                    onChange={stateMutatePointMutateSingleVoyage.onChangePlannedArrival}
                    format={DATE_FORMAT_POINT_SINGLE_VOYAGE}
                    showHoursAndMinutes
                    fullWidth={false}
                    sx={{padding: '0'}}
                    label={t('base.to')}
                />}

                <SwitchCustom
                    checked={stateMutatePointMutateSingleVoyage.hasPlannedDeparture}
                    onChange={stateMutatePointMutateSingleVoyage.onChangeHasPlannedDeparture}
                    label={t('base.hasTimeEnd')}
                />
                {stateMutatePointMutateSingleVoyage.hasPlannedDeparture && <MuiDatePicker
                    value={stateMutatePointMutateSingleVoyage.plannedDeparture}
                    onChange={stateMutatePointMutateSingleVoyage.onChangePlannedDeparture}
                    format={DATE_FORMAT_POINT_SINGLE_VOYAGE}
                    showHoursAndMinutes
                    fullWidth={false}
                    sx={{padding: '0'}}
                    label={t('base.from')}
                />}


            </>}

            {onSave && <PrimaryButtonSingleVoyage
                sx={sx.btn}
                onClick={onSaveFn}
                fullWidth
            >
                {btnLabel || t('base.savePoint')}
            </PrimaryButtonSingleVoyage>}

        </ActionUiHandler>
    );
};

export default BaseMutatePointMutateSingleVoyage;
